import React from "react";

/**
 * ContentMediaSection (reusable)
 * -----------------------------------------------
 * Props:
 * - heading: string                      // main title (e.g., "Azka Rehman" or "Become a Mentor, Make an Impact")
 * - subheading?: string                  // small label under heading (e.g., "Co-Founder CABIL")
 * - body: string                         // paragraph(s)
 * - img: string                          // image URL
 * - reverse?: boolean                    // image on right if true
 * - showDot?: boolean                    // show orange circle behind heading
 * - dotColor?: string                    // color of the circle (default: accentBR)
 * - dotOffset?: { top?: number, left?: number }  // fine-tune dot position (px)
 * - accentTL?: string                    // top-left corner pill color
 * - accentBR?: string                    // bottom-right corner pill color
 * - showCorners?: boolean                // toggle corner pills
 * - cta?: { label: string, href?: string, onClick?: fn } // optional CTA button
 */
export default function TeamBioSection({
  heading,
  subheading = "",
  body,
  img,
  reverse = false,
  showDot = true,
  dotColor,
  dotOffset = { top: -4, left: -16 }, // matches your “behind the name” look
  accentTL = "#2F3AAE",
  accentBR = "#F1652D",
  showCorners = true,
  cta, // { label, href?, onClick? }
}) {
  const dotStyle = {
    backgroundColor: dotColor || accentBR,
    opacity: 0.99,
    top: dotOffset.top,
    left: dotOffset.left,
  };

  const Button = ({ item }) => {
    if (!item?.label) return null;
    const classes =
      "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold " +
      "bg-[#F1652D] text-white hover:brightness-110 transition shadow";
    const icon = (
      <svg width="14" height="14" viewBox="0 0 24 24" className="-mr-0.5">
        <path d="M13 5l7 7-7 7M5 12h14" fill="none" stroke="currentColor" strokeWidth="2" />
      </svg>
    );
    return item.href ? (
      <a href={item.href} className={classes}>{item.label}{icon}</a>
    ) : (
      <button onClick={item.onClick} className={classes}>{item.label}{icon}</button>
    );
  };

  return (
    <section className="bg-white py-12 sm:py-16">
      <div
        className={[
          "mx-auto max-w-6xl px-4 sm:px-6",
          "flex items-start gap-8 lg:gap-14",
          reverse ? "flex-col md:flex-row-reverse" : "flex-col md:flex-row",
        ].join(" ")}
      >
        {/* IMAGE BLOCK */}
        <div className={["relative shrink-0", reverse ? "md:pl-0" : "md:pr-0 md:mr-12"].join(" ")}>
          {showCorners && (
            <>
              {/* top-left pill */}
              <span
                aria-hidden="true"
                className="absolute -top-3 -left-3 h-24 w-24 rounded-xl"
                style={{ backgroundColor: accentTL }}
              />
              {/* bottom-right pill */}
              <span
                aria-hidden="true"
                className="absolute -bottom-4 -right-6 h-44 w-44 rounded-xl"
                style={{ backgroundColor: accentBR }}
              />
            </>
          )}
          {/* photo card */}
          <div className="relative w-[300px] sm:w-[340px] lg:w-[360px] rounded-xl overflow-hidden bg-[#F3EFEA] ring-1 ring-black/5 shadow-sm">
            <img
              src={img}
              alt={heading}
              className="block w-full h-auto object-cover"
              draggable="false"
            />
          </div>
        </div>

        {/* TEXT BLOCK */}
        <div className="min-w-0 flex-1">
          <div className="relative flex items-start gap-3">
            {/* orange circle BEHIND heading (toggleable) */}
            {showDot && (
              <span
                aria-hidden="true"
                className="absolute inline-block h-8 w-8 -mt-4 -ml-2 sm:h-12 sm:w-12 rounded-full z-0"
                style={dotStyle}
              />
            )}
            <div className="relative z-10">
              <h3 className="text-[26px] sm:text-[28px] lg:text-[36px] font-bold leading-[1.15] text-slate-800">
                {heading}
              </h3>
              {subheading && (
                <p className="text-[11px] sm:text-[18px] uppercase tracking-wide text-slate-500">
                  {subheading}
                </p>
              )}
            </div>
          </div>
{/* BODY CONTENT — supports string or array of paragraphs */}
<div className="mt-4 space-y-5 max-w-[68ch]">
  {Array.isArray(body)
    ? body.map((para, i) => (
        <p
          key={i}
          className="text-[18px] leading-8 text-[#696984]"
        >
          {para}
        </p>
      ))
    : (
        <p className="text-[18px] leading-8 text-[#696984]">
          {body}
        </p>
      )}
</div>


          {/* CTA (optional) */}
          {cta?.label && (
            <div className="mt-6">
              <Button item={cta} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
